
import { supabase } from '@/integrations/supabase/client';
import { SitemapURL } from './types';
import { generateSitemapXML } from './utils';

const SITE_URL = 'https://www.grandpal.co';

/**
 * Generates the main sitemap with static pages
 */
export function generateMainSitemap(): string {
  const urls: SitemapURL[] = [
    { loc: SITE_URL, changefreq: 'daily', priority: 1.0 },
    { loc: `${SITE_URL}/about`, changefreq: 'monthly', priority: 0.8 },
    { loc: `${SITE_URL}/nursing-homes`, changefreq: 'daily', priority: 0.9 },
    { loc: `${SITE_URL}/fair-deal-calculator`, changefreq: 'monthly', priority: 0.8 },
    { loc: `${SITE_URL}/waitlist`, changefreq: 'monthly', priority: 0.8 },
    { loc: `${SITE_URL}/press`, changefreq: 'monthly', priority: 0.5 },
    { loc: `${SITE_URL}/careers`, changefreq: 'weekly', priority: 0.6 },
    { loc: `${SITE_URL}/privacy-policy`, changefreq: 'yearly', priority: 0.3 },
    { loc: `${SITE_URL}/terms-of-use`, changefreq: 'yearly', priority: 0.3 },
    { loc: `${SITE_URL}/legal/cookies`, changefreq: 'yearly', priority: 0.3 }
  ];

  return generateSitemapXML(urls);
}

/**
 * Generates a sitemap for all counties
 */
export async function generateCountiesSitemap(): Promise<string> {
  const { data: counties, error } = await supabase
    .from('counties')
    .select('slug, updated_at')
    .order('updated_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching counties:', error);
    throw error;
  }

  console.log(`Found ${counties?.length || 0} counties for sitemap`);

  const urls: SitemapURL[] = counties?.map(county => ({
    loc: `${SITE_URL}/nursing-homes/${county.slug}`,
    lastmod: new Date(county.updated_at || new Date()).toISOString(),
    changefreq: 'weekly',
    priority: 0.8
  })) || [];

  return generateSitemapXML(urls);
}

/**
 * Generates a sitemap for all nursing homes
 */
export async function generateNursingHomesSitemap(): Promise<string> {
  try {
    // Using last_updated_at instead of updated_at
    const { data, error } = await supabase
      .from('nursing_homes')
      .select('nh_slug, county_slug, last_updated_at')
      .order('last_updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching nursing homes:', error);
      throw error;
    }

    console.log(`Found ${data?.length || 0} nursing homes for sitemap`);
    
    // Create safe urls array with type assertion
    const urls: SitemapURL[] = (data || []).map(home => {
      // Ensure we have the required properties
      if (!home.county_slug || !home.nh_slug) {
        console.warn('Missing slug data for nursing home', home);
        return null;
      }
      
      return {
        loc: `${SITE_URL}/nursing-homes/${home.county_slug}/${home.nh_slug}`,
        lastmod: new Date(home.last_updated_at || new Date()).toISOString(),
        changefreq: 'weekly' as const,
        priority: 0.7
      };
    }).filter(Boolean) as SitemapURL[];

    return generateSitemapXML(urls);
  } catch (err) {
    console.error('Error generating nursing homes sitemap:', err);
    // Return empty sitemap if there's an error
    return generateSitemapXML([]);
  }
}

/**
 * Generates a sitemap for guides and blog content
 */
export async function generateGuidesAndBlogSitemap(): Promise<string> {
  const { data: guides, error } = await supabase
    .from('guides')
    .select('slug, updated_at')
    .order('updated_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching guides:', error);
    throw error;
  }

  console.log(`Found ${guides?.length || 0} guides for sitemap`);

  const urls: SitemapURL[] = [
    {
      loc: `${SITE_URL}/blog`,
      changefreq: 'weekly',
      priority: 0.8
    },
    {
      loc: `${SITE_URL}/guides`,
      changefreq: 'weekly',
      priority: 0.8
    },
    ...(guides?.map(guide => ({
      loc: `${SITE_URL}/guides/${guide.slug}`,
      lastmod: new Date(guide.updated_at || new Date()).toISOString(),
      changefreq: 'monthly' as const,
      priority: 0.7
    })) || [])
  ];

  return generateSitemapXML(urls);
}
